import React, { useContext, useEffect, useState } from "react";
import { Box, Button, CircularProgress, Grid, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import LiveEditErrorModal from "../../components/Modals/LiveEditErrorModal";
import AddIcon from "@mui/icons-material/Add";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import CTABlock from "../../components/CTABlock";
import CreateInvoiceModal from "../../components/Modals/CreateInvoiceModal";
import InvoiceFiltersModal from "../../components/Modals/InvoiceFiltersModal";
import InvoiceHistoryTable from "./components/InvoiceHistoryTable";
import Store, { activeRouteKeys } from "../../Store/Store";
import SpecSpinner from "../../components/SpecSpinner";
import useVendors from "../../hooks/useVendors";
import inventoryClient from "../../clients/inventoryClient";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import SnackBarSuccess from "../../components/SnackBarSuccess";
import SnackBarError from "../../components/SnackBarError";
import useEstablishmentSubscription from "../../hooks/useEstablishmentSubscription";
import SubscriptionScreenLock from "../../components/SubscriptionScreenLock";

const Invoices = () => {
  const [errorModalOpen, toggleErrorModalOpen] = useState(false);
  const [invoiceModalOpen, toggleInvoiceModalOpen] = useState(false);
  const [filtersModalOpen, toggleFiltersModalOpen] = useState(false);
  const [invoiceFilters, setInvoiceFilters] = useState({
    startDate: null,
    endDate: null,
    vendorIds: [],
  });
  const [searchMode, setSearchMode] = useState("invoiceNumber");
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [alertSuccess, setAlertSuccess] = useState(undefined);
  const [errorMessage, setErrorMessage] = useState(undefined);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const store = useContext(Store);
  const { establishmentid } = useParams();
  const queryClient = useQueryClient();
  const [{ isLoading: isLoadingVendorData, data: vendorData }, vendorMap] =
    useVendors(establishmentid);
  const { data: subscriptionState, isLoading: isLoadingSubscriptionState } = useEstablishmentSubscription(establishmentid);

  const hasActiveModalFilters =
    invoiceFilters.startDate ||
    invoiceFilters.endDate ||
    invoiceFilters.vendorIds?.length > 0;

  const hasActiveFilters =
    hasActiveModalFilters || debouncedSearchText.trim().length > 0;

  useEffect(() => {
    store.updateActiveRoute(
      activeRouteKeys.invoices,
      `spec/${establishmentid}/invoices`
    );
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  const buildFiltersPayload = () => {
    const payload = {};

    if (invoiceFilters.startDate && invoiceFilters.endDate) {
      // Convert dates to UTC ISO strings for the API
      const startDateUtc = new Date(invoiceFilters.startDate);
      startDateUtc.setUTCHours(0, 0, 0, 0);

      const endDateUtc = new Date(invoiceFilters.endDate);
      endDateUtc.setUTCHours(23, 59, 59, 999);

      payload.startDate = startDateUtc.toISOString();
      payload.endDate = endDateUtc.toISOString();
    }

    if (invoiceFilters.vendorIds?.length > 0) {
      payload.vendorIds = invoiceFilters.vendorIds;
    }

    const trimmedSearch = debouncedSearchText.trim();
    if (trimmedSearch) {
      payload[searchMode] = trimmedSearch;
    }

    return payload;
  };

  const { data: invoiceData, isLoading: isLoadingInvoices, isFetching: isFetchingInvoices } = useQuery({
    queryKey: [
      "invoices",
      {
        establishmentId: establishmentid,
        page: page,
        filters: hasActiveFilters ? buildFiltersPayload() : null,
        searchText: debouncedSearchText.trim(),
        searchMode,
      },
    ],
    queryFn: hasActiveFilters
      ? inventoryClient.getEstablishmentInvoicesWithFilters
      : inventoryClient.getEstablishmentInvoices,
    keepPreviousData: true,
    enabled: !!subscriptionState?.isActive,
  });

  const createInvoiceMutation = useMutation(inventoryClient.createInvoice, {
    onSuccess: (result) => {
      setPage(1);
      queryClient.invalidateQueries([
        "invoices",
        { establishmentId: establishmentid, page: 1 },
      ]);
      toggleInvoiceModalOpen(false);
      navigate(result.invoice.id);
    },
  });

  const handleCreateInvoice = async (payload) => {
    payload.establishmentId = establishmentid;
    await createInvoiceMutation.mutateAsync(payload);
  };

  const handleApplyFilters = (filters) => {
    setInvoiceFilters(filters);
    setPage(1); // Reset to first page when filters change
  };

  if (isLoadingInvoices || isLoadingVendorData || isLoadingSubscriptionState) {
    return <SpecSpinner open text="Loading Invoices..." />;
  }

  return (
    <>
      <Grid
        container
        sx={{
          width: "100%",
          maxWidth: "2440px",
          padding: 4,
          marginTop: 6,
        }}
      >
        {/* {isErrorLoadingStockCounts && (
          <Typography variant="h6" sx={{ color: "red" }}>
            There was an error fetching stock count data, please refresh
          </Typography>
        )} */}
        <>
          <Grid
            container
            sx={{
              maxWidth: "2440px",
              marginBottom: 6,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Grid item xs={12} md={8}>
              <Typography
                variant="h2"
                sx={{ fontSize: { xs: 20, sm: 20, md: 28 } }}
              >
                Invoices
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
                marginTop: { xs: 4, md: 0 },
              }}
            >
              <Button
                variant="contained"
                sx={{
                  borderRadius: 2,
                  padding: 4,
                  height: "40px",
                }}
                onClick={() => toggleInvoiceModalOpen(true)}
              >
                <AddIcon sx={{ fontSize: { xs: 14, sm: 14, md: 20 } }} />
                <Typography
                  variant="smallButton"
                  sx={{ fontSize: { xs: 10, sm: 12, md: 14 } }}
                >
                  Create New
                </Typography>
              </Button>
            </Grid>
          </Grid>

          {/* Search by Toggle + Input */}
          <Box sx={{ marginBottom: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
              <Typography variant="body1" sx={{ marginRight: 2, fontWeight: 600 }}>
                Search by:
              </Typography>
              <ToggleButtonGroup
                value={searchMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode !== null) {
                    setSearchMode(newMode);
                    setPage(1);
                  }
                }}
                size="small"
                sx={{ borderRadius: 100 }}
              >
                <ToggleButton
                  value="invoiceNumber"
                  sx={{
                    textTransform: "none",
                    px: 2,
                    py: 0.5,
                    fontSize: "12px",
                    borderRadius: "100px 0 0 100px",
                    "&.Mui-selected": {
                      backgroundColor: (theme) => theme.palette.primary[800],
                      color: (theme) => theme.palette.pure.white,
                      "&:hover": {
                        backgroundColor: (theme) => theme.palette.primary[800],
                      },
                    },
                  }}
                >
                  Invoice No.
                </ToggleButton>
                <ToggleButton
                  value="checkNumber"
                  sx={{
                    textTransform: "none",
                    px: 2,
                    py: 0.5,
                    fontSize: "12px",
                    borderRadius: "0 100px 100px 0",
                    "&.Mui-selected": {
                      backgroundColor: (theme) => theme.palette.primary[800],
                      color: (theme) => theme.palette.pure.white,
                      "&:hover": {
                        backgroundColor: (theme) => theme.palette.primary[800],
                      },
                    },
                  }}
                >
                  Check No.
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TextField
                size="small"
                placeholder={searchMode === "invoiceNumber" ? "Search by invoice number" : "Search by check number"}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{ width: "350px" }}
                InputProps={{
                  startAdornment: (
                    <SearchOutlinedIcon sx={{ fontSize: 20, marginRight: 1, color: (theme) => theme.palette.terrain[400] }} />
                  ),
                }}
              />
              {isFetchingInvoices && searchText.trim().length > 0 && (
                <CircularProgress size={20} sx={{ marginLeft: 2 }} />
              )}
            </Box>
          </Box>

          <Box sx={{ width: "100%" }}>
            <Grid
              container
              sx={{
                marginBottom: 6,
                marginLeft: 0,
                display: "flex",
                justifyContent: "space-between",
                alignitems: "center",
                maxWidth: "2440px",
              }}
            >
              <Grid
                item
                xs={12}
                md={8}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: { xs: 2, sm: 2, md: 0 },
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ fontSize: { xs: 16, sm: 16, md: 20 } }}
                >
                  All Invoices
                </Typography>
              </Grid>
              {/* Filters Button */}
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => toggleFiltersModalOpen(true)}
                  sx={{
                    marginLeft: 2,
                    border: "2px solid",
                    borderColor: hasActiveModalFilters
                      ? (theme) => theme.palette.primary[800]
                      : (theme) => theme.palette.terrain[400],
                    backgroundColor: hasActiveModalFilters
                      ? (theme) => theme.palette.terrain[100]
                      : "transparent",
                  }}
                >
                  <TuneOutlinedIcon sx={{ fontSize: 20, marginRight: 1 }} />
                  <Typography variant="largeButton">Filters</Typography>
                  {hasActiveModalFilters && (
                    <Box
                      sx={{
                        marginLeft: 1,
                        backgroundColor: (theme) => theme.palette.primary[800],
                        color: (theme) => theme.palette.pure.white,
                        borderRadius: "50%",
                        width: "20px",
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                      }}
                    >
                      {(invoiceFilters.startDate && invoiceFilters.endDate ? 1 : 0) +
                        (invoiceFilters.vendorIds?.length > 0 ? 1 : 0)}
                    </Box>
                  )}
                </Button>
              </Grid>
            </Grid>
            {subscriptionState?.isActive ? (
              <Grid container sx={{ marginBottom: 2, width: "100%" }}>
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "2440px",
                }}
              >
                {invoiceData?.invoices.length !== 0 ? (
                  <InvoiceHistoryTable
                    vendorMap={vendorMap}
                    invoices={invoiceData.invoices}
                    totalPages={invoiceData.totalPages}
                    setPage={setPage}
                  />
                ) : (
                  <CTABlock
                    handleCTA={() => toggleInvoiceModalOpen(true)}
                    ButtonImage={AddIcon}
                    buttonText="New Invoice"
                    header="You have not created any invoices yet."
                    subheader="You have no invoices available for this time period."
                  />
                )}
              </Box>
            </Grid>
            ) : null}
          </Box>
        </>
      </Grid>
      <CreateInvoiceModal
        isOpen={invoiceModalOpen}
        toggleModalOpen={toggleInvoiceModalOpen}
        buttonText="Create Invoice"
        headerText="Create New Invoice"
        createInvoice={handleCreateInvoice}
        loading={createInvoiceMutation.isLoading}
        vendors={vendorData?.vendors}
      />
      <LiveEditErrorModal
        isOpen={errorModalOpen}
        toggleErrorModalOpen={toggleErrorModalOpen}
        bodyText="Are you sure you want to navigate back? You will lose any unsaved information."
        headerText="Hold on..."
        buttonText="Go Back"
      />
      <InvoiceFiltersModal
        isOpen={filtersModalOpen}
        toggleModalOpen={toggleFiltersModalOpen}
        currentFilters={invoiceFilters}
        vendors={vendorData?.vendors}
        onApplyFilters={handleApplyFilters}
      />
      <Box>
        <SnackBarSuccess
          alertSuccess={alertSuccess}
          setAlertSuccess={setAlertSuccess}
        />
        <SnackBarError
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
        />
      </Box>
      <SubscriptionScreenLock show={!subscriptionState?.isActive} />
    </>
  );
};

export default Invoices;
